/**
 * Content Slider — frontend behaviour.
 *
 * Mirrors hero-slider.js: the markup ships Swiper's `.swiper` / `.swiper-wrapper`
 * classes but no `.swiper-slide` (so the editor preview just stacks the slides,
 * all editable). On the frontend we tag each content-slide `.swiper-slide`, boot
 * Swiper and render the sticker-style numbered dots into `.swiper-pagination`.
 *
 * The editor (window.wp.data) is skipped — the static stack is its preview.
 * A single slide is not a slideshow: Swiper still boots (for consistent layout)
 * but loop/autoplay stay off and the lone dot is hidden via CSS.
 *
 * Slides wrap whole layout blocks (Zigzag, Hero Subcategory…) whose entrance
 * animations are scroll-driven (GSAP ScrollTrigger on the vertical axis). Inside
 * a horizontally-translated Swiper that vertical trigger never fires correctly,
 * leaving content stuck at opacity:0. To prevent that, content-slide renders a
 * `data-no-scroll-reveal` attribute on each slide (server-side, so it's present
 * before any JS runs — no race with this script); the layout partials read it
 * and render their content statically (the path they already use for
 * prefers-reduced-motion).
 */

import Swiper from "swiper";
import { A11y, Autoplay, Keyboard, Pagination } from "swiper/modules";

function initBlock(root) {
  if (root.dataset.contentSliderInited === "1") return;
  root.dataset.contentSliderInited = "1";

  const viewport = root.querySelector(".bskr-content-slider__viewport");
  const wrapper = root.querySelector(".bskr-content-slider__track");
  if (!viewport || !wrapper) return;

  const slides = Array.from(
    wrapper.querySelectorAll(":scope > .bskr-content-slide")
  );
  if (!slides.length) return;
  slides.forEach((s) => s.classList.add("swiper-slide"));

  const multiple = slides.length > 1;
  root.classList.toggle("has-multiple", multiple);

  // eslint-disable-next-line no-new
  new Swiper(viewport, {
    modules: [Pagination, Autoplay, Keyboard, A11y],
    slidesPerView: 1,
    autoHeight: true,
    loop: multiple,
    speed: 600,
    grabCursor: multiple,
    autoplay: multiple
      ? { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }
      : false,
    keyboard: { enabled: true },
    pagination: {
      el: root.querySelector(".bskr-content-slider__dots"),
      clickable: true,
      bulletClass: "bskr-content-slider__dot",
      bulletActiveClass: "is-active",
      renderBullet(index, className) {
        return (
          `<button type="button" class="${className}" ` +
          `aria-label="Slide ${index + 1}">` +
          `<span class="bskr-content-slider__dot-shape" aria-hidden="true"></span>` +
          `<span class="bskr-content-slider__dot-num">${index + 1}</span>` +
          `</button>`
        );
      },
    },
  });
}

function init() {
  // Skip inside the Gutenberg editor — the static stack is the editor preview.
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  document.querySelectorAll(".bskr-content-slider").forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
